import { E_TIMEOUT, Semaphore, withTimeout } from "async-mutex";
import { applyPatches } from "immer";
import {
	type Actor,
	type PromiseActorLogic,
	createActor,
	createMachine,
	fromPromise,
	waitFor,
} from "xstate";

import { type EnqueueTransactionEvent } from "../../api-canvas-metadata";
import { type StateTransaction } from "../../api-canvas-metadata/StateTransaction";
import {
	type CanvasSpecifier,
	type MessageStepsSinceRequest,
	type StepsSinceResponse,
	makeMetadataMessageTransactionBroadcast,
} from "../../api-types";
import { type ApiCollabResponse, type Env, UnauthorizedError } from "../../http";
import { CollabConnections } from "../../http/Connections";
import { type LoggerInterface } from "../../logger-types";
import { type TransactionResponse } from "../../metadata-collab-api-client";
import {
	stepsSinceAcceptedResponse,
	stepsSinceRejectedResponse,
	transactionRejectedResponse,
	transactionSuccessResponse,
} from "../../metadata-http";
import {
	MomentAuthenticationApiClient,
	Permission,
	authRejectedResponse,
} from "../../moment-graphql-api";
import { GQLError } from "../../moment-graphql-api/graphqlRequest";
import {
	type BroadcastMessage,
	type PageLocalStorageInitializeEvent,
	type TransactionRequest,
} from "../../page-local-storage/api-types";
import {
	type InitializationResponse,
	type LocalStorage,
	initializationFailedResponse,
	initializationRejectionResponse,
	initializationSuccessResponse,
} from "../../page-local-storage/server/index";
import {
	type InitializeMachineEvent,
	type ResolveTransactionOutput,
	type StateSyncServerContext,
	type StateSyncServerMachine,
	makeStateSyncServerMachine,
	waitForTransactionProcessed,
} from "../../state-sync-server";
import { type CollabStorage } from "../../storage";

export class PageLocalStorageServer {
	_authClient: MomentAuthenticationApiClient;
	_storage: CollabStorage;
	private _logger: LoggerInterface;
	private collabConnections: CollabConnections;
	_stateMachine: Actor<StateSyncServerMachine<LocalStorage>>;
	private broadcastFn: (message: BroadcastMessage) => void;
	private canvasSpecifier: CanvasSpecifier | null = null;
	_env: Env;

	// This is initialized with value = 0 as it is expected that
	// APIs that require the initialization of the server to complete will await
	// for this latch to unlock.
	// The initialization routine will set the value to 1 which will signal that
	// the latch is unlocked.
	private initializationLatch = withTimeout(new Semaphore(0), 3_000);

	constructor(opts: {
		env: Env;
		storage: CollabStorage;
		logger: LoggerInterface;
		broadcastFn: (message: BroadcastMessage) => void;
		closeConnection: (connectionID: string) => void;
	}) {
		this._authClient = new MomentAuthenticationApiClient(opts.env);
		this._env = opts.env;
		this._storage = opts.storage;
		this._logger = opts.logger;
		this._stateMachine = createActor(
			makeStateSyncServerMachine({
				fetchState: this.makeFetchStateActor(),
				resolveTransaction: this.makeResolveTransactionCallback(),
				persist: this.emptyPersistActor(),
				publish: createMachine({}),
				logger: opts.logger,
			})
		);
		this.broadcastFn = opts.broadcastFn;
		this.collabConnections = new CollabConnections(opts.closeConnection, opts.logger);
		this._stateMachine.start();
	}

	async onConnect(
		userToken: string,
		canvasID: string,
		pageID: string,
		connectionID: string,
		tabID: string
	): Promise<boolean> {
		try {
			const authorizationHeader = `Bearer ${userToken}`;
			const authorization = await this._authClient.isReadAuthorized({
				authorizationHeader,
				canvasID,
			});
			if (!authorization.hasPermission) {
				this._logger.error(
					"user doesn't have read permissions for canvas. failing ws connection.",
					{
						canvasID,
					}
				);
				this.collabConnections.disconnectUser(authorization.userID);
				return false;
			}
			this.canvasSpecifier = {
				id: canvasID,
				pageId: pageID,
			};
			this._stateMachine.send({
				type: "initialize",
				extra: {
					canvasSpecifier: {
						id: canvasID,
						pageId: pageID,
					},
					authorizationHeader,
				},
			});

			const result = await waitFor(
				this._stateMachine,
				(snap) => !snap.matches("initializing")
			);
			const { version } = result.context;

			if (result.matches("initializationFailed")) {
				const e = result.context.initializationError;
				this._logger.error("initialization failed", {
					error_message: e?.["message"],
				});
				return false;
			}

			if (typeof version !== "number") {
				this._logger.error("version is not a number");
				return false;
			}
			this._logger.info("room initialization success");
			this.initializationLatch.setValue(1);
			this.collabConnections.registerConnected(authorization.userID, connectionID, tabID);
			return true;
		} catch (error) {
			let extras = {} as unknown;
			if (error instanceof GQLError) {
				extras = error.extras;
			}
			if (!(error instanceof UnauthorizedError)) {
				this._logger.error("error on collab connect", {
					error,
					extras,
				});
			}
			return false;
		}
	}

	async onClose(connectionID: string) {
		this.collabConnections.closeConnection(connectionID);
	}

	async initialize(
		requestHeaders: Headers,
		authorizationHeader: string,
		message: PageLocalStorageInitializeEvent
	): Promise<ApiCollabResponse<InitializationResponse>> {
		try {
			const canvasSpecifier = message.canvasSpecifier;
			if (!authorizationHeader) {
				this._logger.error("request does not have auth header");
				return authRejectedResponse(0);
			}
			const authResponse = await this._authClient.isReadAuthorized({
				authorizationHeader,
				canvasID: canvasSpecifier.id,
			});
			if (!authResponse.hasPermission) {
				this._logger.error("user does not have read permissions for initializing room.", {
					canvasID: canvasSpecifier.id,
					userID: authResponse.userID,
					error: authResponse.error,
					canvasSpecifier,
				});
				this.collabConnections.disconnectUser(authResponse.userID);
				return authRejectedResponse(
					0 // failed on initialize.
				);
			}

			if (authResponse.error !== undefined && authResponse.error !== "") {
				this._logger.error("authorization had error", {
					canvasID: canvasSpecifier.id,
					userID: authResponse.userID,
					error: authResponse.error,
					canvasSpecifier,
				});
				this.collabConnections.disconnectUser(authResponse.userID);
				return authRejectedResponse(
					0 // failed on initialize.
				);
			}

			this._stateMachine.send({
				type: "initialize",
				extra: { canvasSpecifier, authorizationHeader },
			});

			const result = await waitFor(
				this._stateMachine,
				(snap) => !snap.matches("initializing")
			);
			const { data, version } = result.context;

			if (result.matches("initializationFailed")) {
				const e = result.context.initializationError;
				this._logger.error("initialization failed", {
					error_message: e?.["message"],
					canvasSpecifier,
				});
				return initializationFailedResponse(
					requestHeaders,
					e?.["message"] ? `: ${e?.["message"]}` : "",
					version,
					data
				);
			}

			if (typeof version !== "number") {
				this._logger.error("version is not a number");
				return initializationFailedResponse(
					requestHeaders,
					"invalid non-number version on initialize",
					version,
					data
				);
			}

			this.canvasSpecifier = canvasSpecifier;
			this._logger.info("room initialization success");
			this.initializationLatch.setValue(1);
			return initializationSuccessResponse(requestHeaders, version, data);
		} catch (e) {
			this._logger.error("room initialization failed due to exception", {
				error_message: e?.["message"],
			});
			// failed on initialize.
			const version = 0;
			if (e instanceof UnauthorizedError) {
				return authRejectedResponse(version);
			}
			return initializationRejectionResponse(requestHeaders, version);
		}
	}

	async enqueueTransaction(
		authorizationHeader: string,
		transactionRequest: TransactionRequest
	): Promise<ApiCollabResponse<TransactionResponse>> {
		try {
			await this.initializationLatch.waitForUnlock(1);
		} catch (error) {
			if (error === E_TIMEOUT) {
				return authRejectedResponse(this._stateMachine.getSnapshot().context.version);
			}
			throw error;
		}
		try {
			if (!this.canvasSpecifier) {
				throw new Error("no canvas specifier for enqueuing transaction");
			}
			const canvasID = this.canvasSpecifier.id;
			const authResponse = await this._authClient.isWriteAuthorized({
				authorizationHeader,
				canvasID,
			});
			if (!authResponse.userPermissions.includes(Permission.READ.valueOf())) {
				this._logger.error(
					"user no longer has write permissions for enqueuing transactions.",
					{
						permissions: authResponse.userPermissions,
					}
				);
				this.collabConnections.disconnectUser(authResponse.userID);
				return authRejectedResponse(this._stateMachine.getSnapshot().context.version);
			}
			if (!authorizationHeader || !authResponse.hasPermission) {
				this._logger.info("user doesn't have permissions for enqueuing transactions", {
					userID: authResponse.userID,
					userPermissions: authResponse.userPermissions,
				});
				return authRejectedResponse(this._stateMachine.getSnapshot().context.version);
			}

			const enqueueEvent: EnqueueTransactionEvent = {
				type: "enqueueTransaction",
				transactionId: transactionRequest.transactionId,
				lastVersionSeen: transactionRequest.lastVersionSeen,
				patches: transactionRequest.patches,
			};
			this._stateMachine.send(enqueueEvent);
			const snap = await waitForTransactionProcessed(
				this._stateMachine,
				transactionRequest.transactionId
			);
			if (!snap.context.lastTransactionProcessed) {
				this._logger.error("last transaction processed is null");
				// We should have a last transaction at this point, but if one doesn't exist, fall back
				// to `unknown` error.
				return transactionRejectedResponse(snap.context.version, {
					type: "error",
					error: "unknown",
					message: "last transaction processed is null, this should not happen",
					transaction: { ...enqueueEvent, id: transactionRequest.transactionId },
				});
			}

			if (snap.context.lastTransactionProcessed.type === "error") {
				this._logger.error("last transaction processed resulted in an error", {
					lastTransaction: snap.context.lastTransactionProcessed.transaction,
					errorType: snap.context.lastTransactionProcessed.error,
					errorMessage: snap.context.lastTransactionProcessed.message,
				});
				return transactionRejectedResponse(
					snap.context.version,
					snap.context.lastTransactionProcessed
				);
			}
			const broadcastMessage = makeMetadataMessageTransactionBroadcast(
				snap.context.version,
				snap.context.lastTransactionProcessed
			);
			this.broadcastFn(broadcastMessage);
			await this.persistLocalData(snap.context.data, snap.context.version);
			return transactionSuccessResponse(
				snap.context.version,
				snap.context.lastTransactionProcessed
			);
		} catch (error) {
			let extras = {} as unknown;
			if (error instanceof GQLError) {
				extras = error.extras;
			}
			this._logger.error("transaction request failed due to exception", {
				error_message: error?.["message"],
				message: error?.["message"], //FIXME: this is to determine fix for: https://github.com/moment-eng/moment/pull/7245/
				extras,
			});
			const snap = this._stateMachine.getSnapshot();
			if (error instanceof UnauthorizedError) {
				return authRejectedResponse(snap.context.version);
			}
			throw error;
		}
	}

	async stepsSinceRequest(
		authorizationHeader: string | null,
		message: MessageStepsSinceRequest
	): Promise<ApiCollabResponse<StepsSinceResponse>> {
		try {
			await this.initializationLatch.waitForUnlock(1);
		} catch (error) {
			if (error === E_TIMEOUT) {
				return authRejectedResponse(this._stateMachine.getSnapshot().context.version);
			}
			throw error;
		}
		try {
			const currentSnapshotContext = this._stateMachine.getSnapshot().context;
			// FIXME: This is write authorized but we likely want read authorization.
			// For now, we are super strict and will dial it back when we have the facilities
			// to make the read check instead.
			if (this.canvasSpecifier) {
				const authResponse = await this._authClient.isReadAuthorized({
					authorizationHeader,
					canvasID: this.canvasSpecifier.id,
				});
				if (!authResponse.hasPermission) {
					this._logger.error(
						"user doesn't have read permissions for steps since request",
						{
							userID: authResponse.userID,
							userPermissions: authResponse.userPermissions,
						}
					);
					this.collabConnections.disconnectUser(authResponse.userID);
					return authRejectedResponse(currentSnapshotContext.version);
				}
			} else {
				// FIXME: need to figure out how to get the canvas id from the state machine
				return authRejectedResponse(currentSnapshotContext.version);
			}

			const snap = this._stateMachine.getSnapshot();

			const versionsInBuffer = snap.context.transactionHistoryBuffer.length;
			const versionsToFetch = snap.context.version - message.version;
			if (versionsToFetch <= 0 || versionsToFetch > versionsInBuffer) {
				this._logger.error(
					"steps since request rejected due to more versions requested than in buffer history",
					{
						versionsInBuffer,
						versionsToFetch,
					}
				);
				return stepsSinceRejectedResponse(
					"requested more versions than are stored in the versions history buffer"
				);
			}
			const versions = snap.context.transactionHistoryBuffer.slice(
				versionsInBuffer - versionsToFetch
			);

			return stepsSinceAcceptedResponse(
				message.version,
				versions.flatMap((v) => v.patches)
			);
		} catch (error) {
			let extras = {} as unknown;
			if (error instanceof GQLError) {
				extras = error.extras;
			}
			this._logger.error("error in processing steps since", {
				error,
				extras,
			});

			if (error instanceof UnauthorizedError) {
				return authRejectedResponse(this._stateMachine.getSnapshot().context.version);
			}
			throw error;
		}
	}

	private makeFetchStateActor(): PromiseActorLogic<
		{
			version: number;
			data: Record<string, unknown>;
		},
		InitializeMachineEvent
	> {
		return fromPromise(async () => {
			const data = await this._storage.getAll();
			const version = await this._storage.getVersion();
			return {
				version,
				data,
			};
		});
	}

	private makeResolveTransactionCallback(): (
		transaction: StateTransaction,
		input: Record<string, unknown>
	) => Promise<ResolveTransactionOutput<Record<string, unknown>>> {
		return async (transaction, input) => {
			const resolvedData = applyPatches(input, transaction.patches);
			return {
				stateTransaction: transaction,
				resolvedData,
			};
		};
	}

	private emptyPersistActor(): PromiseActorLogic<
		void,
		{
			context: StateSyncServerContext<Record<string, unknown>>;
			data: Record<string, unknown>;
		}
	> {
		return fromPromise(async (): Promise<void> => {
			// no persistence required.
			return;
		});
	}

	private async persistLocalData(data: LocalStorage, version: number) {
		try {
			await this._storage.setVersion(version);
			for (const [k, v] of Object.entries(data)) {
				if (v) {
					await this._storage.put(k, v);
				} else {
					await this._storage.delete(k);
				}
			}
			return true;
		} catch (e) {
			this._logger.error("error persisting local data", {
				data,
			});
			return false;
		}
	}
}
